import {
  type FullPurchaseOrderInfo,
  type PatchedUpdatePurchaseOrderDTO,
  PricingTypeEnum,
  type TransportMethodEnum,
  useApiOrdersV1PurchaseOrdersRetrieve,
  WhoFixesDateEnum,
} from 'api';
import { dayjs } from 'date';
import { Form, type InferType, useForm, yupResolver } from 'form';
import { useTranslation } from 'i18n';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, AlertTitle, Box, Button, ConfirmationDialog, Page, Section } from 'ui';

import { PurchaseOrderFormSections } from '../../components';
import { FormContextProvider } from '../../contexts/PurchaseOrderFormContext';
import { useUpdatePurchaseOrder } from '../../hooks';
import { updatePurchaseOrderSchema } from '../../schemas/updatePurchaseOrderSchema';
import { convertOrderMaterialComponentValuesToForm } from '../../utils/materialComponentValue';

const getPurchaseOrderFormDefaults = (purchaseOrder: FullPurchaseOrderInfo): UpdateFormValues => {
  return {
    basel_code_override: purchaseOrder.basel_code_override,
    cash_or_three_month: purchaseOrder.cash_or_three_month ?? undefined,
    collection_site_id: purchaseOrder.collection_site.id,
    comments: purchaseOrder.comments,
    company_payment_term_id: purchaseOrder.company_payment_term_id ?? undefined,
    creation_process: purchaseOrder.creation_process ?? undefined,
    date_fixed: purchaseOrder.date_fixed ?? undefined,
    delivery_site_id: undefined,
    deposit_event_offset: purchaseOrder.deposit_event_offset ? Math.abs(purchaseOrder.deposit_event_offset) : undefined,
    deposit_event_offset_direction: purchaseOrder.deposit_event_offset
      ? purchaseOrder.deposit_event_offset > 0
        ? 1
        : -1
      : 1,
    deposit_payment_event: purchaseOrder.deposit_payment_event ?? undefined,
    deposit_percentage: purchaseOrder.deposit_percentage,
    dirt_level: purchaseOrder.dirt_level,
    documents: undefined,
    due_date_direction: purchaseOrder.due_date_offset > 0 ? 1 : -1,
    due_date_offset: Math.abs(purchaseOrder.due_date_offset),
    exporter_id: purchaseOrder.exporter?.id,
    external_notes: purchaseOrder.external_notes,
    external_reference_number: purchaseOrder.external_reference_number,
    fixed_price: purchaseOrder.date_fixed ? purchaseOrder.price_per_tonne : undefined,
    fixing_date: purchaseOrder.fixing_date ?? undefined,
    fixing_date_end_event: purchaseOrder.fixing_date_end_event ?? undefined,
    fixing_date_end_offset: purchaseOrder.fixing_date_end_offset ?? undefined,
    fixing_date_start_event: purchaseOrder.fixing_date_start_event ?? undefined,
    fixing_date_start_offset: purchaseOrder.fixing_date_start_offset ?? undefined,
    form_id: purchaseOrder.form?.id ?? undefined,
    grade_id: purchaseOrder.grade?.id ?? 0,
    has_caps_and_labels: purchaseOrder.has_caps_and_labels ?? undefined,
    icd_id: undefined,
    incoterm: purchaseOrder.incoterm,
    index: purchaseOrder.index || undefined,
    internal_notes: purchaseOrder.internal_notes,
    is_food_grade: purchaseOrder.is_food_grade ?? undefined,
    is_internal_grade_shown_to_seller: purchaseOrder.is_internal_grade_shown_to_seller,
    is_long_term_contract: purchaseOrder.is_long_term_contract,
    is_variable_pricing: purchaseOrder.pricing_type === PricingTypeEnum.INDEX,
    material_colour_id: purchaseOrder.material_spec?.colour?.id || undefined,
    material_id: purchaseOrder.material.id,
    material_product_id: undefined,
    material_spec_id: purchaseOrder.material_spec.id,
    number_of_loads: Math.ceil(Number(purchaseOrder.tonnes) / Number(purchaseOrder.tonnes_per_load)).toString(),
    origin_id: purchaseOrder.origin?.id,
    payment_before_material_is_released: purchaseOrder.payment_before_material_is_released,
    payment_event: purchaseOrder.payment_event,
    payment_method: purchaseOrder.payment_method!,
    payment_terms: purchaseOrder.payment_terms,
    payment_terms_comments: purchaseOrder.payment_terms_comments ?? undefined,
    port_of_discharge_id: purchaseOrder.port_of_discharge?.id,
    port_of_loading_id: purchaseOrder.port_of_loading?.id,
    price_as_percentage_of_index: purchaseOrder.price_as_percentage_of_index ?? undefined,
    price_per_tonne: purchaseOrder.price_per_tonne,
    price_point: purchaseOrder.price_point ?? undefined,
    producer_id: purchaseOrder.producer.id,
    production_site_id: purchaseOrder.production_site?.id,
    safi_vat: purchaseOrder.safi_vat,
    seller_agent_id: purchaseOrder.seller_agent?.id,
    seller_currency: purchaseOrder.seller_currency,
    seller_id: purchaseOrder.seller.id,
    seller_material_component_values: convertOrderMaterialComponentValuesToForm(
      purchaseOrder.material_component_values || []
    ),
    should_update_material_properties_from_site_material: false,
    site_material_id: purchaseOrder.site_material?.id,
    storage_type: purchaseOrder.storage_type ?? undefined,
    target_collection_date: purchaseOrder.target_collection_date,
    third_party_commission_currency: purchaseOrder.third_party_commission_currency,
    third_party_commission_per_tonne: purchaseOrder.third_party_commission_per_tonne ?? '0',
    tonnage_variance: purchaseOrder.tonnage_variance,
    tonnes: purchaseOrder.tonnes,
    tonnes_per_load: purchaseOrder.tonnes_per_load,
    // Since transport_method was not always required, it can be null for older POs, so we have to do this type casting.
    transport_method: purchaseOrder.transport_method ?? ('' as TransportMethodEnum),
    who_fixes_date: purchaseOrder.who_fixes_date ?? undefined,
  };
};

export type UpdateFormValues = InferType<typeof updatePurchaseOrderSchema>;

export const PurchaseOrderUpdate = () => {
  const { t } = useTranslation('orders');
  const { id: purchaseOrderId } = useParams();

  const { data: purchaseOrderData, isLoading } = useApiOrdersV1PurchaseOrdersRetrieve(purchaseOrderId!);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [fixedPriceDialogOpen, setFixedPriceDialogOpen] = useState(false);

  const form = useForm<UpdateFormValues, unknown, UpdateFormValues>({
    resolver: yupResolver(updatePurchaseOrderSchema),
    values: purchaseOrderData ? getPurchaseOrderFormDefaults(purchaseOrderData) : undefined,
  });

  const { mutate: updateOrder, isPending } = useUpdatePurchaseOrder(form);

  const formRef = useRef<HTMLFormElement>(null);

  const { watch, setValue } = form;
  const siteMaterialId = watch('site_material_id');
  const isVariablePricing = watch('is_variable_pricing');
  const fixedPrice = watch('fixed_price');
  const dateFixed = watch('date_fixed');

  useEffect(() => {
    if (purchaseOrderData && siteMaterialId && purchaseOrderData?.site_material?.id !== siteMaterialId) {
      setValue('should_update_material_properties_from_site_material', true);
    }
  }, [purchaseOrderData, siteMaterialId, setValue]);

  const onSubmit = async (values: UpdateFormValues) => {
    if (!purchaseOrderId) {
      return;
    }
    const dueDateOffset = (values.due_date_offset || 0) * values.due_date_direction;
    const depostEventOffset = (values.deposit_event_offset || 0) * values.deposit_event_offset_direction;
    const targetCollectionDate = dayjs(values.target_collection_date).format('YYYY-MM-DD');
    const fixingDate =
      values.who_fixes_date && values.fixing_date ? dayjs(values.fixing_date).format('YYYY-MM-DD') : undefined;
    const pricePerTonne = values.fixed_price ? values.fixed_price : values.price_per_tonne;
    const pricingType = values.is_variable_pricing ? PricingTypeEnum.INDEX : PricingTypeEnum.FIXED;
    const preliminaryPrice =
      pricingType === PricingTypeEnum.INDEX && pricingType !== purchaseOrderData?.pricing_type
        ? values.price_per_tonne
        : undefined;
    const date_fixed = values.date_fixed ? dayjs(values.date_fixed).format('YYYY-MM-DD') : undefined;

    const orderData = {
      ...values,
      date_fixed,
      deposit_event_offset: depostEventOffset,
      due_date_offset: dueDateOffset,
      fixing_date: fixingDate,
      preliminary_price: preliminaryPrice,
      price_per_tonne: pricePerTonne,
      pricing_type: pricingType,
      target_collection_date: targetCollectionDate,
    } satisfies PatchedUpdatePurchaseOrderDTO;

    if (!values.is_variable_pricing) {
      (
        [
          'index',
          'price_as_percentage_of_index',
          'cash_or_three_month',
          'who_fixes_date',
          'fixing_date',
          'fixing_date_start_event',
          'fixing_date_start_offset',
          'fixing_date_end_event',
          'fixing_date_end_offset',
          'price_point',
        ] as const
      ).forEach((field) => {
        orderData[field] = undefined;
      });
    }

    if (values.who_fixes_date === WhoFixesDateEnum.CUSTOMER || values.who_fixes_date === WhoFixesDateEnum.SAFI) {
      orderData.fixing_date = undefined;
    } else {
      (
        [
          'fixing_date_start_event',
          'fixing_date_start_offset',
          'fixing_date_end_event',
          'fixing_date_end_offset',
        ] as const
      ).forEach((field) => {
        orderData[field] = undefined;
      });
    }

    if (!values.should_update_material_properties_from_site_material) {
      delete values['site_material_id'];
    }

    updateOrder({
      data: orderData,
      slug: purchaseOrderId,
    });
  };

  const handleSubmitButton = () => {
    if (purchaseOrderData?.is_partially_signed) {
      setConfirmationDialogOpen(true);
    } else if (fixedPrice && dateFixed) {
      setFixedPriceDialogOpen(true);
    } else {
      formRef.current?.requestSubmit();
    }
  };

  return !isLoading && purchaseOrderData ? (
    <Page>
      <Page.Title
        title={t('purchaseOrderUpdatePage.title', { values: { orderId: purchaseOrderId } })}
        subtitle={t('purchaseOrderUpdatePage.subtitle')}
      />
      <Page.Divider />

      <Section>
        <Box
          sx={{
            my: 2,
          }}
        >
          {isVariablePricing ? (
            <Alert severity="info">
              <AlertTitle>{t('purchaseOrderUpdatePage.variablePricedOrderTitle')}</AlertTitle>
              {t('purchaseOrderUpdatePage.variablePricedOrderMessage')}
            </Alert>
          ) : null}
        </Box>
        <FormContextProvider
          variant="update"
          defaultValues={getPurchaseOrderFormDefaults(purchaseOrderData)}
          documents={purchaseOrderData.documents ?? []}
          purchaseOrderState={purchaseOrderData.state}
        >
          <Form {...form} onSubmit={onSubmit} formRef={formRef} devtool={false}>
            <PurchaseOrderFormSections />
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'end',
                paddingTop: 4,
              }}
            >
              <ConfirmationDialog
                open={confirmationDialogOpen}
                title={t('purchaseOrderUpdatePage.warningDialog.title')}
                message={t('purchaseOrderUpdatePage.warningDialog.message')}
                handleCancel={() => {
                  setConfirmationDialogOpen(false);
                }}
                handleConfirm={() => {
                  setConfirmationDialogOpen(false);
                  formRef.current?.requestSubmit();
                }}
              />
              <ConfirmationDialog
                open={fixedPriceDialogOpen}
                title={t('purchaseOrderUpdatePage.fixedPriceDialog.title')}
                message={t('purchaseOrderUpdatePage.fixedPriceDialog.message')}
                handleCancel={() => {
                  setFixedPriceDialogOpen(false);
                }}
                handleConfirm={() => {
                  setFixedPriceDialogOpen(false);
                  formRef.current?.requestSubmit();
                }}
              />
              <Button size="large" loading={isPending} onClick={handleSubmitButton}>
                {t('purchaseOrderUpdatePage.submit')}
              </Button>
            </Box>
          </Form>
        </FormContextProvider>
      </Section>
    </Page>
  ) : null;
};
